import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '';

interface SearchHit {
  kind: 'customer' | 'project' | 'report' | 'document';
  id: string;
  title: string;
  subtitle?: string;
  customerId?: string;
  projectId?: string;
  savedReportId?: string;
}

const KIND_META: Record<SearchHit['kind'], { label: string; icon: string; tint: string }> = {
  customer: {
    label: 'Customer',
    tint: 'text-green-600 dark:text-green-400',
    icon: 'M16 14a4 4 0 1 0-8 0M12 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm-8 9c0-2.5 3-4 8-4s8 1.5 8 4',
  },
  project: {
    label: 'Project',
    tint: 'text-blue-600 dark:text-blue-400',
    icon: 'M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z',
  },
  report: {
    label: 'Report',
    tint: 'text-purple-600 dark:text-purple-400',
    icon: 'M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9l-6-6Zm0 0v6h6M8 13h8M8 17h5',
  },
  document: {
    label: 'Document',
    tint: 'text-amber-600 dark:text-amber-400',
    icon: 'M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9l-6-6Zm0 0v6h6',
  },
};

/**
 * Global command palette — opens on Cmd/Ctrl-K. Searches across customers,
 * projects, reports, and documents accessible to the current user. Results
 * navigate directly to the matching record.
 */
const SearchPalette: React.FC = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const requestSeqRef = useRef(0);

  // Global open shortcut. Cmd+K (Mac) / Ctrl+K (Win/Linux).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsOpen((v) => !v);
      }
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen]);

  // Focus + reset state when opening
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setHits([]);
      setActiveIdx(0);
      // Tiny delay so the input is mounted before focus
      window.requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isOpen]);

  // Debounced search — bumps a sequence number so out-of-order responses are
  // discarded if a faster later request lands first.
  useEffect(() => {
    if (!isOpen) return;
    const q = query.trim();
    if (q.length < 2) {
      setHits([]);
      setLoading(false);
      return;
    }
    const seq = ++requestSeqRef.current;
    setLoading(true);
    const t = window.setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/search?q=${encodeURIComponent(q)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('search failed');
        const data = await res.json();
        if (seq !== requestSeqRef.current) return;
        setHits(Array.isArray(data.hits) ? data.hits : []);
        setActiveIdx(0);
      } catch {
        if (seq === requestSeqRef.current) setHits([]);
      } finally {
        if (seq === requestSeqRef.current) setLoading(false);
      }
    }, 180);
    return () => window.clearTimeout(t);
  }, [query, isOpen, token]);

  const onHitClick = useCallback(
    (hit: SearchHit) => {
      setIsOpen(false);
      if (hit.kind === 'report' && hit.savedReportId) {
        navigate(`/workflow-report?reportId=${hit.savedReportId}`);
        return;
      }
      // For customer/project/document, fall back to home with a URL hint the
      // home page already understands (?customerId=&projectId=).
      const params = new URLSearchParams();
      if (hit.customerId) params.set('customerId', hit.customerId);
      if (hit.projectId) params.set('projectId', hit.projectId);
      const search = params.toString();
      navigate(search ? `/?${search}` : '/');
    },
    [navigate]
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(hits.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter' && hits[activeIdx]) {
      e.preventDefault();
      onHitClick(hits[activeIdx]);
    }
  };

  if (!user || !isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/40 flex items-start justify-center pt-24 px-4"
      onClick={() => setIsOpen(false)}
      role="dialog"
      aria-label="Global search"
    >
      <div
        className="w-full max-w-xl bg-white dark:bg-dark-surface rounded-lg shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 dark:border-dark-border">
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search customers, projects, reports, documents…"
            className="flex-1 bg-transparent border-none outline-none text-secondary dark:text-dark-text placeholder-gray-400 text-sm"
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="hidden sm:inline-block text-[10px] font-mono px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded border border-gray-200 dark:border-gray-600">
            esc
          </kbd>
        </div>

        <div className="max-h-96 overflow-y-auto">
          {query.trim().length < 2 ? (
            <div className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400 text-center">
              Start typing to search. Try a customer name, report title, or document.
            </div>
          ) : loading ? (
            <div className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400 text-center">
              Searching…
            </div>
          ) : hits.length === 0 ? (
            <div className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400 text-center">
              No matches.
            </div>
          ) : (
            <ul role="listbox">
              {hits.map((hit, idx) => {
                const meta = KIND_META[hit.kind];
                const active = idx === activeIdx;
                return (
                  <li
                    key={`${hit.kind}-${hit.id}`}
                    role="option"
                    aria-selected={active}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onClick={() => onHitClick(hit)}
                    className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer ${
                      active ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                    }`}
                  >
                    <svg className={`w-4 h-4 flex-shrink-0 ${meta.tint}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <path d={meta.icon} />
                    </svg>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-secondary dark:text-dark-text truncate">
                        {hit.title}
                      </div>
                      {hit.subtitle && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {hit.subtitle}
                        </div>
                      )}
                    </div>
                    <span className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
                      {meta.label}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="px-4 py-2 border-t border-gray-200 dark:border-dark-border text-[11px] text-gray-500 dark:text-gray-400 flex items-center justify-between">
          <span><kbd className="font-mono">↑↓</kbd> navigate · <kbd className="font-mono">↵</kbd> open · <kbd className="font-mono">esc</kbd> close</span>
          <span><kbd className="font-mono">⌘K</kbd> toggle</span>
        </div>
      </div>
    </div>
  );
};

export default SearchPalette;
